/**
 * DrawerTrackerShell — the per-tracker providers around the drawer tabs.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Binom gets its own draft provider; Voluum and Tres Commas share the
 * Standard (Voluum-shaped) draft, read from and applied to their OWN tracker.
 * Everything inside also learns the tracker through FlowTrackerProvider.
 */

import type { FC, ReactNode } from 'react';
import { matchTracker, type FlowOptimizerConfig, type TrackerId } from '@trafficpulse/shared';

import { BinomDrawerProvider } from '@/features/flow-optimizer/tabs/standard/binom';
import { StandardDraftProvider } from '@/features/flow-optimizer/standard-draft';
import { FlowTrackerProvider } from '@/features/flow-optimizer/tracker';

export interface DrawerTrackerShellProps {
  provider: TrackerId;
  campaignId: string;
  draft: FlowOptimizerConfig;
  /** Binom only: whether a tab that reads its campaign draft is showing. */
  binomDrawerActive: boolean;
  children: ReactNode;
}

const DrawerTrackerShell: FC<DrawerTrackerShellProps> = ({
  provider,
  campaignId,
  draft,
  binomDrawerActive,
  children,
}) => {
  const body = <FlowTrackerProvider tracker={provider}>{children}</FlowTrackerProvider>;
  const standard = () => (
    <StandardDraftProvider flowId={draft.flowId} provider={provider}>
      {body}
    </StandardDraftProvider>
  );
  return matchTracker(provider, {
    binom: () => (
      <BinomDrawerProvider
        flowId={draft.flowId}
        campaignId={campaignId}
        killSwitch={draft.killSwitch}
        drawerActive={binomDrawerActive}
      >
        {body}
      </BinomDrawerProvider>
    ),
    voluum: standard,
    'tres-commas': standard,
  });
};

export default DrawerTrackerShell;
